import { Avatar, Box, Flex, HStack, Icon, VStack, Text, Button } from '@chakra-ui/react';
import React from 'react';
import parse from 'html-react-parser';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { BsHeadset } from 'react-icons/bs';
import { IoCheckmarkCircleOutline, IoCloseCircleOutline } from 'react-icons/io5';
import { CustomText } from '../atoms';
import { Settings } from '../../interfaces';
import { BannerWithSearchBar } from './BannerWithSearchBar';
// import { JaggedBorderFooter } from '../atoms/JaggedBorderFooter';

type ChatMessage = {
  sender: string;
  timestamp: string;
  message: string;
  origin: string;
};

export interface TicketData {
  ticketId: string;
  status: 'Active' | 'resolved' | 'rejected';
  messages: ChatMessage[];
}

export interface TicketViewProps {
  showHelperButton?: boolean;
  showRocks?: boolean;
  // showJaggedBorder?: boolean;
  ticketData: TicketData;
  settings: Settings | null;
  handleBackClick: () => void;
  rocksImageUrl?: string;
  backClickHref?: string;
}

export const TicketView: React.FC<TicketViewProps> = ({
  ticketData,
  settings,
  showHelperButton = true,
  showRocks = true,
  // showJaggedBorder = true,
  handleBackClick,
  rocksImageUrl,
  backClickHref,
}) => {
  const status = ticketData.status?.toLowerCase();
  const isResolved = status === 'resolved';
  const isRejected = status === 'rejected';
  const description = `Status - ${ticketData.status}`;
  // eslint-disable-next-line no-nested-ternary
  const statusIcon = isResolved ? (
    <IoCheckmarkCircleOutline color="white" size={18} />
  ) : isRejected ? (
    <IoCloseCircleOutline color="white" size={18} />
  ) : null;
  const label = `Ticket #${ticketData.ticketId}`;
  return (
    <Box>
      <BannerWithSearchBar
        isFullScreen={settings?.configs.isBannerFullScreen}
        px={{
          base: '0vw',
          md: '0vw',
          lg: settings?.configs.isBannerFullScreen ? '0vw' : '15vw',
        }}
        imageSrc={settings?.configs.myTicketPageLogoUrl}
        altText="Contact Us Banner"
        showSearchBar={false}
        bannerTextColor="white"
        placeholderText=""
        showOverlay={undefined}
        bannerText={label}
        subText={
          <HStack justify="center" align="center" spacing={2}>
            <Text color="white" fontSize={{ base: 'sm', md: 'md', lg: 'lg' }}>
              {description}
            </Text>
            {statusIcon}
          </HStack>
        }
        showButton
        buttonText="Back"
        onButtonClick={handleBackClick}
        secondaryAccentColor={settings?.configs.secondaryAccentColor}
        bgColor={settings?.configs.secondaryBgColor}
        showJaggedBorder={settings?.configs.showJaggedBorder}
        buttonHref={backClickHref}
      />
      <Box padding="2rem" maxWidth="1400px" margin="0 auto">
        {/* Messages Section */}
        <VStack align="start" spacing={6} mt={4} w="full">
          {ticketData.messages.map(msg => {
            const isBot = msg.origin === 'helpshift';
            return (
              <HStack
                key={msg.timestamp + msg.sender}
                w="100%"
                justify={isBot ? 'flex-end' : 'flex-start'}
                spacing={3}
              >
                {!isBot && <Avatar size="md" name={msg.sender} bg="gray.300" color="white" />}
                <Box
                  position="relative"
                  w="80%"
                  p={4}
                  bg={
                    isBot
                      ? settings?.configs.helpshiftChatBgColor
                      : settings?.configs.userChatBgColor
                  }
                  borderRadius="md"
                  shadow="md"
                  alignSelf={isBot ? 'flex-end' : 'flex-start'}
                  textAlign={isBot ? 'right' : 'left'}
                  marginLeft={isBot ? 'auto' : '0'}
                  marginRight={isBot ? '0' : 'auto'}
                  _after={{
                    content: `""`,
                    position: 'absolute',
                    bottom: '8px',
                    [isBot ? 'right' : 'left']: '-20px',
                    borderWidth: '10px',
                    borderStyle: 'solid',
                    borderColor: isBot
                      ? `transparent transparent transparent ${settings?.configs.helpshiftChatBgColor}`
                      : `${settings?.configs.userChatBgColor} transparent transparent transparent`,
                    transform: isBot ? 'rotate(0deg)' : 'rotate(90deg)',

                    filter: isBot ? '' : `drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))`,
                  }}
                >
                  <HStack justifyContent="space-between" w="full" mb={2}>
                    {/* Left side: Icon + Sender Name */}
                    <HStack spacing={2}>
                      {isBot && (
                        <Icon
                          as={BsHeadset}
                          color={settings?.configs.secondaryAccentColor}
                          boxSize="1.25rem"
                        />
                      )}
                      <CustomText
                        variant="h3"
                        fontWeight="bold"
                        fontSize={{ base: 'sm', md: 'md' }}
                        color={
                          isBot
                            ? settings?.configs.helpshiftChatTextColor
                            : settings?.configs.userChatTextColor
                        }
                        marginBottom="2"
                      >
                        {msg.sender}
                      </CustomText>
                    </HStack>

                    {/* Right side: Timestamp */}
                    <CustomText
                      variant="body"
                      fontSize="sm"
                      color={
                        isBot
                          ? settings?.configs.helpshiftChatTextColor
                          : settings?.configs.userChatTextColor
                      }
                      marginBottom="2"
                    >
                      {
                        new Date(msg.timestamp).toLocaleDateString('en-US', {
                          month: 'long', // Jan, Feb, Mar (localized)
                          day: '2-digit',
                          year: 'numeric',
                        })
                        // .replace(/(\w+)\s(\d{2}),\s(\d{4})/, '$1-$2-$3')
                      }
                      ,{' '}
                      {new Date(msg.timestamp).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false, // 24-hour format
                      })}{' '}
                    </CustomText>
                  </HStack>
                  {/* <Box borderBottom="1px solid blue" mb={3}  /> */}
                  <Box
                    height="1px"
                    width="100%"
                    bg={isBot ? '#E4E4E4' : `${settings?.configs.gradientAccentColor}`}
                    mb={3}
                  />

                  {/* Message Content */}
                  <CustomText
                    variant="body"
                    fontSize="sm"
                    color={
                      isBot
                        ? settings?.configs.helpshiftChatTextColor
                        : settings?.configs.userChatTextColor
                    }
                    mt="0"
                  >
                    {parse(msg.message)}
                  </CustomText>
                </Box>
                {isBot && (
                  <Avatar
                    size="md"
                    bg="gray.300"
                    icon={
                      <Box as="span" fontSize="lg">
                        👤
                      </Box>
                    }
                  />
                )}
              </HStack>
            );
          })}
        </VStack>
      </Box>
      {showHelperButton && (
        <Box
          width="100%"
          backgroundImage={showRocks ? `url(${rocksImageUrl})` : undefined}
          backgroundRepeat="no-repeat"
          backgroundPosition={{
            base: 'center calc(100% - 30px)',
            sm: 'center calc(100% - 30px)',
            md: 'center calc(100% - 30px)',
            lg: 'center calc(100% + 145px)',
          }}
          backgroundSize="100% auto"
          pt="50px"
          mt="4rem"
          position="relative"
        >
          <Flex justify="center" align="center" pb="120px">
            <a href={backClickHref}>
              <Button
                onClick={e => {
                  const isLeftClick = e.button === 0;
                  const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
                  // SPA routing on normal click
                  if (isLeftClick && !isModifiedClick) {
                    e.preventDefault();
                    handleBackClick();
                  }
                }}
                leftIcon={<ArrowBackIcon />}
                size="md"
                bgColor={settings?.configs.secondaryAccentColor}
                variant="solid"
                fontSize="md"
                padding="8px 16px"
                fontWeight="bold"
                borderRadius="0"
                type="button"
                colorScheme="black" // Default color scheme
              >
                Back
              </Button>
            </a>
          </Flex>
          {/* {showJaggedBorder && (
            <JaggedBorderFooter bgColor={settings?.configs.footerBackgroundColor} />
          )} */}
        </Box>
      )}
    </Box>
  );
};
